import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import { usePendingApprovals } from '../../hooks/usePendingApprovals';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { SearchInput } from '../common/SearchInput';
import { PendingReferenceEditor } from './PendingReferenceEditor';
import { backfillSubmissionsIndex } from '../../services/firebase';
import type { PendingReferenceApproval, ApprovalStatus } from '../../types';
import { normalizeText } from '../../utils/text';

// Helper to safely convert Firestore timestamp to Date
function toDate(timestamp: unknown): Date | null {
  if (!timestamp) return null;
  // If it's a Firestore Timestamp with toDate method
  if (typeof timestamp === 'object' && 'toDate' in timestamp && typeof (timestamp as { toDate: unknown }).toDate === 'function') {
    return (timestamp as { toDate: () => Date }).toDate();
  }
  // If it's a plain object with seconds (serialized Timestamp)
  if (typeof timestamp === 'object' && 'seconds' in timestamp) {
    return new Date((timestamp as { seconds: number }).seconds * 1000);
  }
  // If it's already a Date
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return null;
}

// Helper to format timestamp safely
function formatTimestamp(timestamp: unknown): string {
  const date = toDate(timestamp);
  if (!date) return '-';
  try {
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return '-';
  }
}

export function ApprovalList() {
  const [backfilling, setBackfilling] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ApprovalStatus | undefined>(
    'pending'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApproval, setSelectedApproval] =
    useState<PendingReferenceApproval | null>(null);

  const { approvals, loading, error, pendingCount } = usePendingApprovals({
    status: statusFilter,
  });

  const filteredApprovals = approvals.filter((approval) => {
    if (!searchQuery) return true;
    const query = normalizeText(searchQuery);
    return (
      normalizeText(approval.brandName ?? '').includes(query) ||
      normalizeText(approval.reference ?? '').includes(query) ||
      normalizeText(approval.model ?? '').includes(query) ||
      normalizeText(approval.collection ?? '').includes(query)
    );
  });

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const r = await backfillSubmissionsIndex();
      const msg =
        `Backfill complete: ${r.scanned} scanned, ${r.pendingPatched} patched, ` +
        `${r.indexWritten} index entries written` +
        (r.collisions.length ? `, ${r.collisions.length} collision(s) flagged` : '');
      if (r.collisions.length) {
        console.warn('Submissions index collisions:', r.collisions);
        toast(msg, { icon: '⚠️' });
      } else {
        toast.success(msg);
      }
    } catch (err) {
      console.error('Backfill failed:', err);
      toast.error('Backfill failed — see console');
    } finally {
      setBackfilling(false);
    }
  };

  const getStatusBadge = (status: ApprovalStatus) => {
    const variants: Record<ApprovalStatus, 'warning' | 'success' | 'danger' | 'default'> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
      // Gray: the three accents are spoken for, and a dismissal is not a
      // judgement on the submission — the reference was simply already there.
      dismissed_duplicate: 'default',
    };
    const labels: Record<ApprovalStatus, string> = {
      pending: 'pending',
      approved: 'approved',
      rejected: 'rejected',
      dismissed_duplicate: 'already in database',
    };
    return <Badge variant={variants[status]}>{labels[status]}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Error: {error}
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Pending Reference Approvals
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {pendingCount} pending approval{pendingCount !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={handleBackfill}
            loading={backfilling}
            title="Ensure every pending submission has a contentKey and submissions_index entry"
          >
            Backfill index
          </Button>
          <select
            value={statusFilter || 'all'}
            onChange={(e) =>
              setStatusFilter(
                e.target.value === 'all'
                  ? undefined
                  : (e.target.value as ApprovalStatus)
              )
            }
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="dismissed_duplicate">Already in database</option>
          </select>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <SearchInput
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
          placeholder="Search by brand, reference, model..."
        />
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brand
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reference
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Collection
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Submitted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredApprovals.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No approvals found
                  </td>
                </tr>
              ) : (
                filteredApprovals.map((approval) => (
                  <tr
                    key={approval.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedApproval(approval)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {approval.imageUrls?.[0] && (
                          <img
                            src={approval.imageUrls[0]}
                            alt=""
                            className="h-10 w-10 rounded-lg object-cover mr-3"
                          />
                        )}
                        <span className="font-medium text-gray-900">
                          {approval.brandName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {approval.reference || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {approval.model}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {approval.collection || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(approval.submittedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(approval.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedApproval(approval);
                        }}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor Modal */}
      {selectedApproval && (
        <PendingReferenceEditor
          approval={selectedApproval}
          onClose={() => setSelectedApproval(null)}
          onSuccess={() => setSelectedApproval(null)}
        />
      )}
    </div>
  );
}
